import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Alert } from '@/components/ui'
import { api, ApiError } from '@/internationalization/api-client'
import { useT } from '@/internationalization/i18n'
import { useSessionStore } from '@/stores'
import type { Tenant, User } from '@/types'

function safeDestination(value: string | null) {
  return value?.startsWith('/') && !value.startsWith('//') ? value : '/'
}

/** Completes the code + PKCE flow without ever putting tokens in the URL. */
export function SsoCallbackPage() {
  const t = useT()
  const navigate = useNavigate()
  const [query] = useSearchParams()
  const signIn = useSessionStore((state) => state.signIn)
  const started = useRef(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (started.current) return
    started.current = true
    const code = query.get('code')
    if (!code) {
      setError(t('ssoCallback.noCode'))
      return
    }

    void api
      .post<{ token: string; user: User; tenant: Tenant }>('/auth/sso/exchange', { code })
      .then((session) => {
        signIn(session)
        navigate(safeDestination(query.get('returnTo')), { replace: true })
      })
      .catch((reason: unknown) => {
        setError(
          reason instanceof ApiError
            ? reason.message
            : t('ssoCallback.couldNotComplete'),
        )
      })
  }, [navigate, query, signIn, t])

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight text-fg">{t('ssoCallback.title')}</h1>
      {error ? (
        <Alert tone="danger" title={t('ssoCallback.errorTitle')}>
          {error}
          {t('ssoCallback.errorSuffix')}
        </Alert>
      ) : (
        <p className="mt-2 text-base text-fg-muted">{t('ssoCallback.verifying')}</p>
      )}
    </div>
  )
}
